import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConical, PhoneCall, Receipt, ShoppingBag, Store, SwatchBook } from 'lucide-react';

import { cn } from '@/lib/utils';
import { StatusBadge } from '../../../components/design-system/admin/StatusBadge';
import { STATUS_TONE, upperKey } from './request-format';
import type { RequestKind, RequestStatus } from './types';

export const KIND_ICONS: Record<RequestKind, React.ComponentType<{ className?: string }>> = {
    sample: SwatchBook,
    visit: Store,
    quote: Receipt,
    callback: PhoneCall,
    buy: ShoppingBag,
};

/** A request's kind as a grey line glyph in a rounded square — the inbox's way of telling them apart at a glance. */
export function KindTile({ kind, size = 'md', className }: { kind: RequestKind; size?: 'sm' | 'md'; className?: string }) {
    const Icon = KIND_ICONS[kind] ?? Receipt;
    return (
        <span
            className={cn(
                'flex shrink-0 items-center justify-center rounded-lg border bg-background text-muted-foreground',
                size === 'sm' ? 'size-7' : 'size-9',
                className,
            )}
            aria-hidden
        >
            <Icon className={size === 'sm' ? 'size-3.5' : 'size-4'} />
        </span>
    );
}

/** The status as a dot and a word. */
export function RequestStatusBadge({ status, className }: { status: RequestStatus; className?: string }) {
    const { t } = useTranslation();
    return (
        <StatusBadge tone={STATUS_TONE[status] ?? 'neutral'} className={className}>
            {t(`SHOPPER_REQUESTS.STATUS.${upperKey(status)}`)}
        </StatusBadge>
    );
}

/** "Test": a request our own team sent, kept apart from real shoppers. */
export function TestBadge({ title }: { title?: string }) {
    const { t } = useTranslation();
    return (
        <span
            className="inline-flex h-5 shrink-0 items-center gap-1 rounded-md border px-1.5 text-[11px] text-muted-foreground"
            title={title || t('SHOPPER_REQUESTS.TEST_HINT')}
            data-testid="sr-test-badge"
        >
            <FlaskConical className="size-3" aria-hidden />
            {t('SHOPPER_REQUESTS.TEST')}
        </span>
    );
}
